import React, { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle2, Download, ShoppingBag, Zap, ArrowRight, Truck, Copy, ShieldCheck, Mail } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import confetti from 'canvas-confetti';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext'; 
import { useTheme } from '../context/ThemeContext';

const Success = () => {
  const location = useLocation();
  const { clearCart } = useCart();
  const { user } = useAuth(); 
  const { isDarkMode } = useTheme();

  // 🔥 VVIP: Order Details Capture (Database ID priority for Tracking)
  const orderInfo = useMemo(() => {
    const order = location.state?.order;
    const items = order?.products || [];
    
    // Tracking ke liye Database ki actual _id chahiye
    const dbId = order?._id || location.state?.orderId; 

    const calculatedTotal = order?.total || items.reduce((acc, item) => acc + (item.price * (item.quantity || 1)), 0);
    
    return {
      id: dbId, // Ye link banane ke kaam aayega
      orderNumber: order?.orderNumber || dbId || "WTH-ARCHIVE",
      items: items,
      total: calculatedTotal
    };
  }, [location.state]);

  // Invoice display ke liye format ID
  const safeOrderId = useMemo(() => {
    const backupId = localStorage.getItem("last_order_id");
    const idToUse = orderInfo.orderNumber || backupId;

    if (!idToUse || idToUse === "WTH-ARCHIVE") return "WTH-PENDING";
    
    const num = String(idToUse).toUpperCase();
    return num.startsWith('WTH-') ? num : `WTH-${num.slice(-8)}`;
  }, [orderInfo]);

  useEffect(() => {
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: isDarkMode ? ['#FFFFFF', '#10b981', '#4F4F4F'] : ['#000000', '#10b981', '#A1A1AA'],
      disableForReducedMotion: true 
    });

    if (orderInfo.id) {
      localStorage.setItem("last_order_id", orderInfo.id);
    }

    const timer = setTimeout(() => {
      clearCart(); 
    }, 2000); 

    return () => clearTimeout(timer);
  }, [isDarkMode, orderInfo.id]);

  const copyToClipboard = () => {
    if (safeOrderId.includes("PENDING")) return;
    navigator.clipboard.writeText(safeOrderId);
    toast.success("ID COPIED", {
      style: { borderRadius: '15px', background: isDarkMode ? '#111' : '#fff', color: isDarkMode ? '#fff' : '#000', border: '1px solid #10b981' }
    });
  };

  const downloadReceipt = () => {
    try {
      const doc = new jsPDF();
      const items = orderInfo.items;

      // Header Black Box
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(22);
      doc.setFont("courier", "bold");
      doc.text("WE THRIFT ARCHIVE", 20, 25);
      
      // Details
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      doc.text(`INVOICE NO: ${safeOrderId}`, 20, 50);
      doc.text(`DATE: ${new Date().toLocaleDateString()}`, 20, 55);
      doc.text(`CUSTOMER: ${user?.username || "Guest Member"}`, 20, 60);

      doc.line(20, 75, 190, 75);
      doc.text("ITEM DESCRIPTION", 20, 82);
      doc.text("TOTAL", 170, 82);
      doc.line(20, 85, 190, 85);

      let yPos = 95;
      if (items.length > 0) {
        items.forEach((p) => {
          doc.text(`${(p.name || p.title || 'Product').toUpperCase()} (x${p.quantity || 1})`, 20, yPos);
          doc.text(`Rs. ${p.price}`, 170, yPos);
          yPos += 10;
        });
      } else {
        doc.text("ARCHIVE ASSET PURCHASE", 20, yPos);
        doc.text(`Rs. ${orderInfo.total}`, 170, yPos);
        yPos += 10;
      }

      doc.line(20, yPos + 5, 190, yPos + 5);
      doc.setFontSize(14);
      doc.setFont("courier", "bold");
      doc.text(`GRAND TOTAL: Rs. ${orderInfo.total}`, 115, yPos + 20);

      doc.save(`INVOICE_${safeOrderId}.pdf`);
      toast.success("RECEIPT SAVED");
    } catch (err) {
      toast.error("Receipt error");
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 py-20 overflow-hidden relative ${isDarkMode ? 'bg-black text-white' : 'bg-white text-black'}`}>
      <Toaster position="bottom-right" />
      
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className={`absolute -top-24 -left-24 w-96 h-96 rounded-full blur-[120px] opacity-20 ${isDarkMode ? 'bg-emerald-500' : 'bg-emerald-200'}`}></div>
        <div className={`absolute -bottom-24 -right-24 w-96 h-96 rounded-full blur-[120px] opacity-10 ${isDarkMode ? 'bg-blue-500' : 'bg-blue-200'}`}></div>
      </div>

      <div className="max-w-3xl w-full text-center relative z-10">
        <div className="mb-8 inline-block">
            <div className={`relative p-8 rounded-[2.5rem] border backdrop-blur-3xl shadow-2xl ${isDarkMode ? 'bg-zinc-900/50 border-white/10 shadow-emerald-500/5' : 'bg-zinc-50 border-black/5 shadow-black/5'}`}>
              <CheckCircle2 size={60} className="mx-auto text-emerald-500 animate-[bounce_2s_infinite]" />
              <div className="absolute -top-2 -right-2 bg-emerald-500 text-black p-1.5 rounded-full shadow-lg">
                <ShieldCheck size={16} />
              </div>
            </div>
        </div>

        <div className="space-y-4 mb-12">
          <h1 className="text-5xl md:text-8xl font-[1000] uppercase italic tracking-tighter leading-none">
            ORDER <span className="text-emerald-500">SECURED</span>
          </h1>
          <p className="text-zinc-500 font-bold uppercase tracking-[0.4em] text-[11px]">
            LOG_ID: {user?.username || "ENTITY_NULL"} // {new Date().toLocaleTimeString()}
          </p>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-12 p-2 border rounded-[2rem] ${isDarkMode ? 'bg-zinc-900/30 border-white/5' : 'bg-zinc-50 border-black/5'}`}>
          <div onClick={copyToClipboard} className={`p-8 rounded-[1.5rem] border transition-all cursor-pointer group ${isDarkMode ? 'bg-black border-white/5 hover:border-emerald-500/50' : 'bg-white border-black/5 hover:border-emerald-500/50'}`}>
            <span className="text-[9px] font-black text-zinc-500 uppercase block mb-3 tracking-widest text-left">ARCHIVE_REFERENCE</span>
            <div className="flex items-center justify-between">
              <p className="text-2xl font-black tracking-tight font-mono text-emerald-500 uppercase italic">{safeOrderId}</p>
              <Copy size={18} className="text-zinc-500 group-hover:text-emerald-500 transition-colors" />
            </div>
          </div>

          <div className={`p-8 rounded-[1.5rem] border flex flex-col justify-center text-left ${isDarkMode ? 'bg-black border-white/5' : 'bg-white border-black/5'}`}>
            <span className="text-[9px] font-black text-zinc-500 uppercase block mb-3 tracking-widest">LOGISTICS_STATUS</span>
            <div className="flex items-center gap-3 font-black text-sm uppercase italic">
              <div className="flex h-3 w-3 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
              </div>
              DISPATCHING_SOON
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <button onClick={downloadReceipt} className={`flex-1 px-8 py-5 rounded-2xl font-black uppercase text-[11px] tracking-widest border transition-all active:scale-95 flex items-center justify-center gap-3 ${isDarkMode ? 'bg-zinc-900 border-white/10 hover:bg-zinc-800' : 'bg-zinc-100 border-black/10 hover:bg-zinc-200'}`}>
              <Download size={18} /> DOWNLOAD_INVOICE
            </button>
            
            {/* 🔥 FIXED DYNAMIC LINK FOR TRACKING */}
            <Link 
              to={orderInfo.id ? `/track/${orderInfo.id}` : "/track-order"} 
              className={`flex-1 px-8 py-5 rounded-2xl font-black uppercase text-[11px] tracking-widest border transition-all active:scale-95 flex items-center justify-center gap-3 ${isDarkMode ? 'bg-emerald-500 text-black border-emerald-400' : 'bg-black text-white border-black'}`}
            >
              <Truck size={18} /> TRACK_ORDER
            </Link>
          </div>
          
          <Link to="/products" className={`w-full px-12 py-5 rounded-2xl font-black uppercase text-[11px] tracking-widest transition-all active:scale-95 flex items-center justify-center gap-3 group ${isDarkMode ? 'bg-white text-black hover:bg-emerald-50' : 'bg-black text-white hover:bg-zinc-900'}`}>
            <ShoppingBag size={18} /> RETURN_TO_SHOP <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Success;